import { arrayOf, bool, func, node } from 'prop-types';
import React, { useState } from 'react';
import Select from 'react-select';
import { Button, Col, Container, FormGroup, Input } from 'reactstrap';

import Association from '../../associations/Association';
import CurrencyInput from '../../generic/CurrencyInput';
import { flash, tapFlashError } from '../../generic/flash-messages';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import withProducts from '../../products/withProducts/with-products';
import { productShape } from '../../prop-types';
import HELP_TEXTS from '../../quote-editor/utils/help-texts';
import api from '../../REST/api';
import withApiRequest from '../../REST/with-api-request';

const WIDTHS = {
  main: [
    { label: 3, content: 9 },
    { label: 3, content: 9 },
  ],
  budget: { label: 3, input: 4, content: { offset: 4, size: 5 } },
};

function generateRandomSFID(length = 18) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const initialFormState = {
  budgets: {
    netClientBudgetExternal: '',
    wkzBudget: '',
    coInvest: '',
    freeServiceValue: '',
  },
  product: null,
};

function SalesforceContractGenerator({
  children,
  products,
  onRequest,
  ...rest
}) {
  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

  const [campaignName, setCampaignName] = useState('');

  const [accountId, setAccountId] = useState(null);

  const [productList, setProductList] = useState([]);
  const [currentEntry, setCurrentEntry] = useState(initialFormState);

  const handleInputChange = e => {
    const { name, value } = e.target;

    if (name in initialFormState.budgets) {
      setCurrentEntry(prevEntry => ({
        ...prevEntry,
        budgets: {
          ...prevEntry.budgets,
          [name]: value,
        },
      }));
    } else {
      setCurrentEntry(prevEntry => ({
        ...prevEntry,
        [name]: value,
      }));
    }
  };

  const handleProductChange = selectedOption => {
    setCurrentEntry(prevEntry => ({
      ...prevEntry,
      product: selectedOption,
    }));
  };

  const handleAddProduct = () => {
    if (!currentEntry.product) {
      tapFlashError()({
        message: 'Please select a product before adding.',
      });
      return;
    }
    const newProductEntry = { ...currentEntry, id: Date.now() };
    setProductList(prevList => [...prevList, newProductEntry]);

    setCurrentEntry(initialFormState);
  };

  const handleRemoveProduct = idToRemove => {
    setProductList(prevList => prevList.filter(item => item.id !== idToRemove));
  };

  const handleAccountChange = selectedOption => {
    setAccountId(selectedOption.target.value);
  };

  const productOptions = products.map(item => ({
    label: `${item.name} - ${item.business}, ${item.productGroup}`,
    value: item.code,
    product: item,
  }));

  const buildQuoteLines = productList.map(item => ({
    target_market: [
      'DE',
      'NL',
      'BE',
      'AT',
      'FR',
      'DK',
      'ES',
      'FI',
      'IT',
      'PL',
      'SE',
      'CZ',
      'IE',
      'LT',
    ],
    target_gender: ['MEN'],
    start_date: '2026-01-28T00:00:00.000Z',
    sf_id: generateRandomSFID(),
    product: {
      sf_id: generateRandomSFID(),
      debug: true,
      code: item.product.value,
    },
    fees: {
      original_fee: 0.0,
      adjusted_fee: 0.0,
    },
    end_date: '2026-05-02T00:00:00.000Z',
    categories: ['Outlet', 'Premium', 'Beauty', 'Clothes'],
    budgets: {
      wkz_budget: {
        currency_iso: 'EUR',
        amount: item.budgets.wkzBudget,
      },
      net_client_budget_external: {
        currency_iso: 'EUR',
        amount: item.budgets.netClientBudgetExternal,
      },
      net_client_budget: {
        currency_iso: 'EUR',
        amount: 100.0,
      },
      net_budget: {
        currency_iso: 'EUR',
        amount: 135.0,
      },
      free_service: {
        currency_iso: 'EUR',
        amount: item.budgets.freeServiceValue,
      },
      co_invest: {
        currency_iso: 'EUR',
        amount: item.budgets.coInvest,
      },
    },
  }));

  const generateContract = ({ accountName = 'Illy' } = {}) => {
    const payload = {
      data_type: 'salesforce.crm.zms-opportunity-information',
      data_op: 'U',
      data: {
        sf_id: generateRandomSFID(),
        sales_unit: 'Sports & Premium',
        sales_team: 'Sports',
        sales_channel: 'MANAGED',
        quote: {
          sf_id: generateRandomSFID(),
          purchase_order_number: '6877276876446',
          name: 'Illy-Illy-Customer Proposition-4OCT23  (V3)',
          meta: {
            last_modified_at: '2023-10-04T14:17:46.000Z',
            created_at: '2023-10-04T14:12:20.000Z',
          },
          flexibility_terms: 'FULL_FLEXIBILITY',
          commercial_incentive: 'NEW_TO_ZALANDO_BRAND_LAUNCH_PACKAGE',
          budgets: {
            gross_offer_value: {
              currency_iso: 'EUR',
              amount: 636374636.36,
            },
            free_service_value: {
              currency_iso: 'EUR',
              amount: 11000,
            },
            fee_discount: {
              currency_iso: 'EUR',
              amount: 236363636.36,
            },
            co_invest_used: {
              currency_iso: 'EUR',
              amount: 200000000,
            },
          },
          quote_lines: buildQuoteLines,
          allowed_flexibilities: ['ADD_REMOVE_MARKETS'],
        },
        parent_sf_id: null,
        opportunity_owner: {
          name: 'Kerem Bayar',
          ldap_name: 'kbayar',
          email: 'kerem.bayar@zalando.de',
        },
        net_client_budget: {
          currency_iso: 'EUR',
          amount: 103424.34,
        },
        negotiated_by: 'ZMS',
        name: campaignName,
        meta: {
          last_modified_at: '2023-10-04T14:14:12.000Z',
          created_at: '2023-10-04T09:27:20.000Z',
        },
        comment: 'adding some text here to check if it is sent out',
        commercial_package_name: 'abc',
        commercial_package_id: '0072000000AIGioI',
        campaign: {
          type: 'LOYALTY_PROGRAM',
          start_date: '2026-01-28T00:00:00.000Z',
          objective: 'PERFORMANCE',
          end_date: '2026-05-02T00:00:00.000Z',
          campaing_name: campaignName,
        },
        brands: [
          {
            sf_id: generateRandomSFID(),
            name: 'Pepe Jeans',
            code: 'PE1',
          },
        ],
        billing_type: 'TEST',
        account: {
          tax_numbers: [],
          sf_id: '0013L00000cC30fQAC',
          name: accountName,
          is_po_number_mandatory: false,
          creditor_number: 'K71631',
          business_partner_id: '0010015639',
          business_model: 'WHOLESALE',
        },
      },
    };

    const data = { payload, accountId };

    return onRequest({
      data,
    }).then(() => flash('Contract Submitted Successfully'));
  };

  return (
    <Container fluid {...rest}>
      <h4>Budget Overview</h4>
      <FormGroup row>
        <TooltipLabel text="Campaign name" colWidth={WIDTHS.budget.label} />
        <Col sm={WIDTHS.budget.content}>
          <Input
            id="sf-name"
            name="name"
            value={campaignName}
            onChange={e => setCampaignName(e.target.value)}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <TooltipLabel text="Account" colWidth={WIDTHS.budget.label} />
        <Col sm={WIDTHS.budget.content}>
          <Association
            id="sf-accountId"
            name="accountId"
            value={accountId}
            type="account"
            showIcon
            verbose
            onChange={handleAccountChange}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <TooltipLabel
          text="Net Client Budget External"
          colWidth={WIDTHS.main[0].label}
        />
        <Col sm={WIDTHS.budget.content}>
          <CurrencyInput
            id="sf-netClientBudgetExternal"
            name="netClientBudgetExternal"
            value={currentEntry.budgets.netClientBudgetExternal}
            onChange={handleInputChange}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <TooltipLabel text="WKZ Budget" colWidth={WIDTHS.budget.label} />
        <Col sm={WIDTHS.budget.content}>
          <CurrencyInput
            id="sf-wkzBudget"
            name="wkzBudget"
            value={currentEntry.budgets.wkzBudget}
            onChange={handleInputChange}
          />
        </Col>
      </FormGroup>

      <FormGroup row>
        <TooltipLabel text="Co-Invest used" colWidth={WIDTHS.budget.label} />
        <Col sm={WIDTHS.budget.content}>
          <CurrencyInput
            id="sf-coInvest"
            name="coInvest"
            value={currentEntry.budgets.coInvest}
            onChange={handleInputChange}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <TooltipLabel
          text="Free Service Value"
          colWidth={WIDTHS.budget.label}
        />
        <Col sm={WIDTHS.budget.content}>
          <CurrencyInput
            id="sf-freeServiceValue"
            name="freeServiceValue"
            value={currentEntry.budgets.freeServiceValue}
            onChange={handleInputChange}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <TooltipLabel text="Select Product" colWidth={WIDTHS.budget.label} />
        <Col sm={WIDTHS.budget.content}>
          <Select
            id="sf-product"
            name="product"
            options={productOptions}
            value={currentEntry.product}
            placeholder="Add product…"
            onChange={handleProductChange}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </Col>
      </FormGroup>
      <Button
        color="primary"
        className="mr-2"
        onClick={generateContract}
        disabled={!campaignName || !accountId || !productList.length}
      >
        Submit
      </Button>
      <Button
        onClick={handleAddProduct}
        disabled={!currentEntry.product}
        className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Add Product Combination
      </Button>

      <div className="mt-5">
        <h4 className="text-xl font-bold text-gray-700">Added Products</h4>
        {productList.length === 0 ? (
          <p className="text-gray-500 mt-2">No products added yet.</p>
        ) : (
          <div className="space-y-4 mt-4">
            {productList.map(item => (
              <div
                key={item.id}
                className="bg-gray-50 p-4 rounded-lg shadow-sm border flex justify-between items-center"
              >
                <div>
                  <p className="font-bold text-indigo-600">
                    {item.product.value}
                  </p>
                  <div className="text-xs text-gray-500 mt-1">
                    <span>
                      Net Client Budget External: €
                      {item.budgets.netClientBudgetExternal || 0}
                    </span>{' '}
                    |<span> WKZ: €{item.budgets.wkzBudget || 0}</span> |
                    <span> Co-Invest: €{item.budgets.coInvest || 0}</span> |
                    <span>
                      {' '}
                      Free Service Value: €{item.budgets.freeServiceValue || 0}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handleRemoveProduct(item.id)}
                  className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-md hover:bg-red-600"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
}

SalesforceContractGenerator.propTypes = {
  children: node,
  products: arrayOf(productShape.isRequired),
  onRequest: func.isRequired,
  loading: bool.isRequired,
};

SalesforceContractGenerator.defaultProps = {
  children: null,
  products: arrayOf(productShape.isRequired),
};

const SalesforceWithRequest = withApiRequest({
  query: api.createSFContract,
  error: 'flash',
  successMessage: 'Contract Submitted Successfully',
})(SalesforceContractGenerator);

export default withProducts({})(SalesforceWithRequest);
